import React, {useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import StyledQueryBar from 'components/organisms/Bars/QueryBar/QueryBar.styles';
import Box from 'components/atoms/Layout/Box/Box';
import utils from 'helpers/utils';
import AdvancedQuery from 'components/organisms/Queries/AdvancedQuery/AdvancedQuery';
import AssistantQuery from 'components/organisms/Queries/AssistantQuery/AssistantQuery';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import useMediaQuery from '@mui/material/useMediaQuery';
import Restore from '@mui/icons-material/Restore';
import searchUtils from 'helpers/search';
import DynamicForm from '@mui/icons-material/DynamicForm';
import ConfirmDialog from 'components/organisms/Dialogs/ConfirmDialog/ConfirmDialog';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';

const QueryBar = React.forwardRef((props, ref) => {
  const {
    terms,
    wizard,
    examples,
    similarity,
    query,
    state,
    count,
    topIds,
    onChange,
    onReset,
    filterGroups,
    fieldData,
    isLoading,
    initialised,
    SimpleQueryProps,
    AssistantQueryProps,
    AdvancedQueryProps,
    ...innerProps
  } = useComponentProps(props, 'QueryBar', {
    static: ['initialised'],
    variable: ['type']
  });

  const innerRef = useRef(null);
  const queryFormRef = useRef(null);

  const dialogControl = useDialogControl();

  const xsDown = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const lsDown = useMediaQuery((theme) => theme.breakpoints.down('ls'));

  const [internalState, setInternalState] = useState({
    pending: null
  });

  const queryBar = useMemo(() => {
    return {
      refs: {
        ref: innerRef
      },
      state: {
        ...internalState
      }
    }
  }, [internalState]);

  useImperativeHandle(ref, () => queryBar);

  const onResetEvent = useEffectEvent(onReset);
  const hasActiveQuery = searchUtils.filterCount(query) || state?.assistantConversation?.length > 0;
  const activeQueryType = query?.assistant ? 'assistant' : (query?.advanced ? 'advanced' : 'assistant');
  const displayQueryType = queryBar.state.pending ?? activeQueryType;
  const actions = useMemo(() => {
    const actions = [];

    const assistant = displayQueryType === 'assistant'
    const advanced = displayQueryType === 'advanced';

    if (assistant) {
      actions.push({
        label: 'Show search',
        icon: DynamicForm,
        auth: utils.createAuth({attribute: `database.query.advanced`}),
        onClick: () => {
          setInternalState((current) => utils.updater({
            pending: 'advanced'
          }, true)(current));
        }
      });
    }

    if (['advanced'].includes(displayQueryType)) {
      actions.push({
        label: 'Assistant',
        icon: AutoAwesome,
        auth: utils.createAuth({attribute: 'database.query.assistant'}),
        onClick: () => {
          setInternalState((current) => utils.updater({
            pending: 'assistant'
          }, true)(current));
        }
      });
    }

    if (onResetEvent) {
      if (!(assistant && lsDown) && !(advanced && xsDown)) {
        actions.push({
          label: 'Reset',
          icon: Restore,
          onClick: () => {
            const handleConfirm = () => {
              onResetEvent?.();
            }

            if (hasActiveQuery) {
              dialogControl.show(<ConfirmDialog question="Are you sure you want to reset the search?"
                                                explanation="The search will be reset and your changes will be lost"
                                                onConfirm={handleConfirm}/>, true);
            } else {
              handleConfirm();
            }
          }
        });
      }
    }

    return actions;
  }, [displayQueryType, dialogControl, xsDown, lsDown, hasActiveQuery, onResetEvent]);

  const handleQueryChange = (query, terms, wizard, examples, similarity) => {
    onChange?.(query, terms, wizard, examples, similarity);
  }

  useEffect(() => {
    setInternalState((current) => {
      return utils.updater({pending: current.pending === activeQueryType ? null : current.pending}, true)(current);
    });
  }, [activeQueryType]);

  const termsMemo = useEffectItem(terms);
  const examplesMemo = useEffectItem(examples);
  const onChangeEvent = useEffectEvent(onChange);
  useEffect(() => {
    if (initialised) {
      if (!utils.isDefined(query)) {
        const newQuery = searchUtils.assistantQuery(query, termsMemo, wizard, examplesMemo, similarity);
        onChangeEvent?.(newQuery, termsMemo, wizard, examplesMemo, similarity);
      } else if (displayQueryType === 'advanced' && (!query.advanced || query.assistant)) {
        const newQuery = searchUtils.advancedQuery(query);
        onChangeEvent?.(newQuery, termsMemo, wizard, examplesMemo, similarity);
      } else if (displayQueryType === 'assistant' && !query.assistant) {
        const newQuery = searchUtils.assistantQuery(query);
        onChangeEvent?.(newQuery, termsMemo, wizard, examplesMemo, similarity);
      }
    }
  }, [initialised, query, termsMemo, wizard, examplesMemo, similarity, displayQueryType, onChangeEvent]);

  const renderContent = () => {
    if (displayQueryType === 'assistant') {
      return <AssistantQuery ref={queryFormRef}
                             className="QueryBar-assistant"
                             terms={terms}
                             wizard={wizard}
                             examples={examples}
                             similarity={similarity}
                             query={query}
                             state={state}
                             count={count}
                             topIds={topIds}
                             isLoading={isLoading}
                             initialised={initialised}
                             fieldData={fieldData}
                             actions={actions}
                             onChange={handleQueryChange}
                             {...AssistantQueryProps}/>
    } else if (displayQueryType === 'advanced') {
      return <AdvancedQuery ref={queryFormRef}
                            className="QueryBar-advanced"
                            terms={terms}
                            wizard={wizard}
                            examples={examples}
                            similarity={similarity}
                            query={query}
                            filterGroups={filterGroups}
                            initialised={initialised}
                            fieldData={fieldData}
                            actions={actions}
                            onChange={handleQueryChange}
                            {...AdvancedQueryProps}/>
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className, {
    type: displayQueryType
  });

  return <StyledQueryBar ref={innerRef} {...innerProps}>
    <Box className="QueryBar-content">
      <Box className="QueryBar-query">
        {initialised ? renderContent() : null}
      </Box>
    </Box>
  </StyledQueryBar>
});

QueryBar.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  terms: PropTypes.array,
  wizard: PropTypes.string,
  examples: PropTypes.array,
  query: PropTypes.object,
  state: PropTypes.object,
  count: PropTypes.number,
  topIds: PropTypes.array,
  similarity: PropTypes.string,
  onChange: PropTypes.func,
  onReset: PropTypes.func,
  filterGroups: PropTypes.array,
  fieldData: PropTypes.object,
  isLoading: PropTypes.bool,
  initialised: PropTypes.bool,
  SimpleQueryProps: PropTypes.object,
  AssistantQueryProps: PropTypes.object,
  AdvancedQueryProps: PropTypes.object
};

export default QueryBar;
